import React, { useEffect, useRef, useState } from "react";
import "./CropperModal.css";

const CANVAS_SIZE = 400;

const getFitTransform = (image) => {
  const fitScale = Math.min(
    CANVAS_SIZE / image.width,
    CANVAS_SIZE / image.height,
  );
  return {
    scale: fitScale,
    position: {
      x: (CANVAS_SIZE - image.width * fitScale) / 2,
      y: (CANVAS_SIZE - image.height * fitScale) / 2,
    },
    minScale: fitScale * 0.2,
    maxScale: Math.max(fitScale * 5, 2),
  };
};

const CropperModal = (props) => {
  const { image, show, onHide, setCroppedImage } = props;
  const canvasRef = useRef(null);
  const lastTouchRef = useRef(null);
  const pinchDistanceRef = useRef(null);
  const isDraggingRef = useRef(false);
  const scaleLimitsRef = useRef({ minScale: 0.1, maxScale: 2 });
  const applyScaleRef = useRef(null);

  const [transform, setTransform] = useState({
    scale: 1,
    x: 0,
    y: 0,
  });
  const [isImageReady, setIsImageReady] = useState(false);

  useEffect(() => {
    const handleTouchStart = (event) => {
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    };

    document.body.classList.add("disable-touch-zoom");
    document.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });

    return () => {
      document.body.classList.remove("disable-touch-zoom");
      document.removeEventListener("touchstart", handleTouchStart);
    };
  }, []);

  useEffect(() => {
    if (!show || !isImageReady) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [show, isImageReady]);

  useEffect(() => {
    if (!image || !image.complete || !image.naturalWidth) {
      setIsImageReady(false);
      return;
    }

    const fit = getFitTransform(image);
    scaleLimitsRef.current = {
      minScale: fit.minScale,
      maxScale: fit.maxScale,
    };
    setTransform({
      scale: fit.scale,
      x: fit.position.x,
      y: fit.position.y,
    });
    setIsImageReady(true);
  }, [image]);

  useEffect(() => {
    if (!show || !isImageReady) return;

    const canvas = canvasRef.current;
    if (!canvas || !image) return;

    const context = canvas.getContext("2d");
    if (!context) return;

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(
      image,
      transform.x,
      transform.y,
      image.width * transform.scale,
      image.height * transform.scale,
    );
  }, [image, transform, show, isImageReady]);

  const clampScale = (nextScale) => {
    const { minScale, maxScale } = scaleLimitsRef.current;
    return Math.min(maxScale, Math.max(minScale, nextScale));
  };

  // Zoom toward the canvas center so +/- keeps the middle of the view stable.
  const applyScale = (nextScaleOrUpdater) => {
    setTransform((prev) => {
      const nextScale =
        typeof nextScaleOrUpdater === "function"
          ? nextScaleOrUpdater(prev.scale)
          : nextScaleOrUpdater;
      const clamped = clampScale(nextScale);
      if (clamped === prev.scale || prev.scale === 0) return prev;

      const center = CANVAS_SIZE / 2;
      const ratio = clamped / prev.scale;

      return {
        scale: clamped,
        x: center - (center - prev.x) * ratio,
        y: center - (center - prev.y) * ratio,
      };
    });
  };
  applyScaleRef.current = applyScale;

  // Non-passive wheel listener so we can block page scroll and zoom smoothly.
  useEffect(() => {
    if (!show || !isImageReady) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const onWheel = (event) => {
      event.preventDefault();
      event.stopPropagation();

      let delta = event.deltaY;
      if (event.deltaMode === 1) delta *= 16; // lines → pixels
      if (event.deltaMode === 2) delta *= CANVAS_SIZE; // pages → pixels

      // Multiplicative zoom stays smooth for both mouse wheel and trackpad.
      const zoomFactor = Math.exp(-delta * 0.0012);
      applyScaleRef.current?.((prevScale) => prevScale * zoomFactor);
    };

    canvas.addEventListener("wheel", onWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", onWheel);
  }, [show, isImageReady]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const originX = transform.x;
    const originY = transform.y;

    const onMouseMove = (moveEvent) => {
      moveEvent.preventDefault();
      setTransform((prev) => ({
        ...prev,
        x: originX + (moveEvent.clientX - startX),
        y: originY + (moveEvent.clientY - startY),
      }));
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const getTouchDistance = (touches) => {
    const touch1 = touches[0];
    const touch2 = touches[1];
    return Math.sqrt(
      (touch1.clientX - touch2.clientX) ** 2 +
        (touch1.clientY - touch2.clientY) ** 2,
    );
  };

  const handleTouchStart = (e) => {
    e.preventDefault();

    if (e.touches.length === 1) {
      const touch = e.touches[0];
      lastTouchRef.current = { x: touch.clientX, y: touch.clientY };
      isDraggingRef.current = true;
      pinchDistanceRef.current = null;
    }

    if (e.touches.length === 2) {
      isDraggingRef.current = false;
      lastTouchRef.current = null;
      pinchDistanceRef.current = getTouchDistance(e.touches);
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault();

    if (e.touches.length === 1 && isDraggingRef.current && lastTouchRef.current) {
      const touch = e.touches[0];
      const deltaX = touch.clientX - lastTouchRef.current.x;
      const deltaY = touch.clientY - lastTouchRef.current.y;
      lastTouchRef.current = { x: touch.clientX, y: touch.clientY };

      setTransform((prev) => ({
        ...prev,
        x: prev.x + deltaX,
        y: prev.y + deltaY,
      }));
    }

    if (e.touches.length === 2 && pinchDistanceRef.current) {
      const distance = getTouchDistance(e.touches);
      const ratio = distance / pinchDistanceRef.current;
      pinchDistanceRef.current = distance;
      applyScale((prevScale) => prevScale * ratio);
    }
  };

  const handleTouchEnd = () => {
    lastTouchRef.current = null;
    pinchDistanceRef.current = null;
    isDraggingRef.current = false;
  };

  const handleCrop = () => {
    onHide();
    const canvas = canvasRef.current;
    if (!canvas) return;

    const croppedCanvas = document.createElement("canvas");
    const ctx = croppedCanvas.getContext("2d");
    if (!ctx) return;

    croppedCanvas.width = canvas.width;
    croppedCanvas.height = canvas.height;
    ctx.clearRect(0, 0, croppedCanvas.width, croppedCanvas.height);
    ctx.globalCompositeOperation = "destination-over";
    ctx.drawImage(canvas, 0, 0);
    setCroppedImage(croppedCanvas.toDataURL("image/png"));
  };

  const handleZoomIn = (e) => {
    e.preventDefault();
    applyScale((prevScale) => prevScale * 1.05);
  };

  const handleZoomIn5x = (e) => {
    e.preventDefault();
    applyScale((prevScale) => prevScale * 1.2);
  };

  const handleZoomOut = (e) => {
    e.preventDefault();
    applyScale((prevScale) => prevScale * 0.95);
  };

  const handleZoomOut5x = (e) => {
    e.preventDefault();
    applyScale((prevScale) => prevScale * 0.8);
  };

  const handleReset = (e) => {
    e.preventDefault();
    if (!image) return;

    const fit = getFitTransform(image);
    scaleLimitsRef.current = {
      minScale: fit.minScale,
      maxScale: fit.maxScale,
    };
    setTransform({
      scale: fit.scale,
      x: fit.position.x,
      y: fit.position.y,
    });
  };

  if (!show || !image || !isImageReady) return null;

  return (
    <div
      className="cropper-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cropperModalTitle"
    >
      <div className="cropper-modal-card cropmodal">
        <div className="cropper-modal-header">
          <div className="cropper-modal-title">
            <span className="cropper-modal-title-bar" aria-hidden="true" />
            <h5 id="cropperModalTitle">Crop Image</h5>
          </div>
          <button
            type="button"
            className="cropper-modal-close"
            aria-label="Close"
            onClick={onHide}
          >
            ×
          </button>
        </div>

        <div className="cropper-modal-body">
          <div className="cropper-canvas-frame">
            <canvas
              ref={canvasRef}
              width={CANVAS_SIZE}
              height={CANVAS_SIZE}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            />
          </div>
        </div>

        <div className="cropper-modal-footer">
          <div className="cropper-zoom-group" role="group" aria-label="Zoom controls">
            <button
              type="button"
              className="cropper-btn cropper-btn-zoom"
              onClick={handleZoomIn}
              title="Zoom in"
              aria-label="Zoom in"
            >
              +
            </button>
            <button
              type="button"
              className="cropper-btn cropper-btn-zoom"
              onClick={handleZoomOut}
              title="Zoom out"
              aria-label="Zoom out"
            >
              -
            </button>
            <button
              type="button"
              className="cropper-btn cropper-btn-zoom"
              onClick={handleZoomIn5x}
              title="Zoom in fast"
              aria-label="Zoom in fast"
            >
              + (5x)
            </button>
            <button
              type="button"
              className="cropper-btn cropper-btn-zoom"
              onClick={handleZoomOut5x}
              title="Zoom out fast"
              aria-label="Zoom out fast"
            >
              - (5x)
            </button>
            <button
              type="button"
              className="cropper-btn cropper-btn-reset"
              onClick={handleReset}
              title="Reset image"
              aria-label="Reset image"
            >
              Reset
            </button>
          </div>

          <div className="cropper-action-group">
            <button
              type="button"
              className="cropper-btn cropper-btn-secondary"
              onClick={onHide}
            >
              Close
            </button>
            <button
              type="button"
              className="cropper-btn cropper-btn-primary"
              onClick={handleCrop}
            >
              Save Image
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CropperModal;
